import type { ElementType, HTMLAttributes, ReactElement } from "react";
import styles from "./Stack.module.css";

export type StackGap = 1 | 2 | 3 | 4 | 6 | 8 | 12;
export type StackAlign = "start" | "center" | "end" | "stretch";
export type StackJustify = "start" | "center" | "between" | "end";

export interface StackProps extends HTMLAttributes<HTMLElement> {
  /** Layout axis. `column` (default) stacks vertically; `row` lays out horizontally. */
  direction?: "row" | "column";
  /** Space between children, from the spacing scale. */
  gap?: StackGap;
  align?: StackAlign;
  justify?: StackJustify;
  /** Allow items to wrap onto multiple lines (row layouts). */
  wrap?: boolean;
  as?: ElementType;
}

/**
 * One-dimensional layout primitive over flexbox with token-driven `gap`. The
 * workhorse for composing sections without scattering margins.
 */
export function Stack({
  direction = "column",
  gap = 4,
  align = "stretch",
  justify = "start",
  wrap = false,
  as: Component = "div",
  className,
  children,
  ...rest
}: StackProps): ReactElement {
  const classes = [
    styles.stack,
    styles[direction],
    styles[`gap-${gap}`],
    styles[`align-${align}`],
    styles[`justify-${justify}`],
    wrap ? styles.wrap : undefined,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
